import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button, Col, Form, FormGroup, Input, InputGroup, Row, Table } from 'reactstrap';
import { JhiItemCount, JhiPagination, Translate, getPaginationState, translate } from 'react-jhipster';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSort, faSortDown, faSortUp } from '@fortawesome/free-solid-svg-icons';
import { ASC, DESC, ITEMS_PER_PAGE, SORT } from 'app/shared/util/pagination.constants';
import { overridePaginationStateWithQueryParams } from 'app/shared/util/entity-utils';
import { useAppDispatch, useAppSelector } from 'app/config/store';

import { getEntities, searchEntities } from './vitals.reducer';

export const Vitals = () => {
  const dispatch = useAppDispatch();

  const pageLocation = useLocation();
  const navigate = useNavigate();

  const [search, setSearch] = useState('');
  const [paginationState, setPaginationState] = useState(
    overridePaginationStateWithQueryParams(getPaginationState(pageLocation, ITEMS_PER_PAGE, 'id'), pageLocation.search),
  );

  const vitalsList = useAppSelector(state => state.vitals.entities);
  const loading = useAppSelector(state => state.vitals.loading);
  const totalItems = useAppSelector(state => state.vitals.totalItems);

  const getAllEntities = () => {
    if (search) {
      dispatch(
        searchEntities({
          query: search,
          page: paginationState.activePage - 1,
          size: paginationState.itemsPerPage,
          sort: `${paginationState.sort},${paginationState.order}`,
        }),
      );
    } else {
      dispatch(
        getEntities({
          page: paginationState.activePage - 1,
          size: paginationState.itemsPerPage,
          sort: `${paginationState.sort},${paginationState.order}`,
        }),
      );
    }
  };

  const startSearching = e => {
    if (search) {
      setPaginationState({
        ...paginationState,
        activePage: 1,
      });
      dispatch(
        searchEntities({
          query: search,
          page: paginationState.activePage - 1,
          size: paginationState.itemsPerPage,
          sort: `${paginationState.sort},${paginationState.order}`,
        }),
      );
    }
    e.preventDefault();
  };

  const clear = () => {
    setSearch('');
    setPaginationState({
      ...paginationState,
      activePage: 1,
    });
    dispatch(getEntities({}));
  };

  const handleSearch = event => setSearch(event.target.value);

  const sortEntities = () => {
    getAllEntities();
    const endURL = `?page=${paginationState.activePage}&sort=${paginationState.sort},${paginationState.order}`;
    if (pageLocation.search !== endURL) {
      navigate(`${pageLocation.pathname}${endURL}`);
    }
  };

  useEffect(() => {
    sortEntities();
  }, [paginationState.activePage, paginationState.order, paginationState.sort, search]);

  useEffect(() => {
    const params = new URLSearchParams(pageLocation.search);
    const page = params.get('page');
    const sort = params.get(SORT);
    if (page && sort) {
      const sortSplit = sort.split(',');
      setPaginationState({
        ...paginationState,
        activePage: +page,
        sort: sortSplit[0],
        order: sortSplit[1],
      });
    }
  }, [pageLocation.search]);

  const sort = p => () => {
    setPaginationState({
      ...paginationState,
      order: paginationState.order === ASC ? DESC : ASC,
      sort: p,
    });
  };

  const handlePagination = currentPage =>
    setPaginationState({
      ...paginationState,
      activePage: currentPage,
    });

  const handleSyncList = () => {
    sortEntities();
  };

  const getSortIconByFieldName = (fieldName: string) => {
    const sortFieldName = paginationState.sort;
    const order = paginationState.order;
    if (sortFieldName !== fieldName) {
      return faSort;
    }
    return order === ASC ? faSortUp : faSortDown;
  };

  return (
    <div>
      <h2 id="vitals-heading" data-cy="VitalsHeading">
        <Translate contentKey="tinyMedicRestApp.vitals.home.title">Vitals</Translate>
        <div className="d-flex justify-content-end">
          <Button className="me-2" color="info" onClick={handleSyncList} disabled={loading}>
            <FontAwesomeIcon icon="sync" spin={loading} />{' '}
            <Translate contentKey="tinyMedicRestApp.vitals.home.refreshListLabel">Refresh List</Translate>
          </Button>
          <Link to="/vitals/new" className="btn btn-primary jh-create-entity" id="jh-create-entity" data-cy="entityCreateButton">
            <FontAwesomeIcon icon="plus" />
            &nbsp;
            <Translate contentKey="tinyMedicRestApp.vitals.home.createLabel">Create new Vitals</Translate>
          </Link>
        </div>
      </h2>
      <Row>
        <Col sm="12">
          <Form onSubmit={startSearching}>
            <FormGroup>
              <InputGroup>
                <Input
                  type="text"
                  name="search"
                  defaultValue={search}
                  onChange={handleSearch}
                  placeholder={translate('tinyMedicRestApp.vitals.home.search')}
                />
                <Button className="input-group-addon">
                  <FontAwesomeIcon icon="search" />
                </Button>
                <Button type="reset" className="input-group-addon" onClick={clear}>
                  <FontAwesomeIcon icon="trash" />
                </Button>
              </InputGroup>
            </FormGroup>
          </Form>
        </Col>
      </Row>
      <div className="table-responsive">
        {vitalsList && vitalsList.length > 0 ? (
          <Table responsive>
            <thead>
              <tr>
                <th className="hand" onClick={sort('id')}>
                  <Translate contentKey="tinyMedicRestApp.vitals.id">Id</Translate> <FontAwesomeIcon icon={getSortIconByFieldName('id')} />
                </th>
                <th className="hand" onClick={sort('systolic')}>
                  <Translate contentKey="tinyMedicRestApp.vitals.systolic">Systolic</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('systolic')} />
                </th>
                <th className="hand" onClick={sort('diastolic')}>
                  <Translate contentKey="tinyMedicRestApp.vitals.diastolic">Diastolic</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('diastolic')} />
                </th>
                <th className="hand" onClick={sort('bpMethod')}>
                  <Translate contentKey="tinyMedicRestApp.vitals.bpMethod">Bp Method</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('bpMethod')} />
                </th>
                <th className="hand" onClick={sort('pulse')}>
                  <Translate contentKey="tinyMedicRestApp.vitals.pulse">Pulse</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('pulse')} />
                </th>
                <th className="hand" onClick={sort('oximetryMethod')}>
                  <Translate contentKey="tinyMedicRestApp.vitals.oximetryMethod">Oximetry Method</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('oximetryMethod')} />
                </th>
                <th className="hand" onClick={sort('oximetry')}>
                  <Translate contentKey="tinyMedicRestApp.vitals.oximetry">Oximetry</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('oximetry')} />
                </th>
                <th className="hand" onClick={sort('respiratoryRate')}>
                  <Translate contentKey="tinyMedicRestApp.vitals.respiratoryRate">Respiratory Rate</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('respiratoryRate')} />
                </th>
                <th className="hand" onClick={sort('bodyTemp')}>
                  <Translate contentKey="tinyMedicRestApp.vitals.bodyTemp">Body Temp</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('bodyTemp')} />
                </th>
                <th className="hand" onClick={sort('bodySurfaceArea')}>
                  <Translate contentKey="tinyMedicRestApp.vitals.bodySurfaceArea">Body Surface Area</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('bodySurfaceArea')} />
                </th>
                <th className="hand" onClick={sort('painScore')}>
                  <Translate contentKey="tinyMedicRestApp.vitals.painScore">Pain Score</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('painScore')} />
                </th>
                <th className="hand" onClick={sort('liverFunction')}>
                  <Translate contentKey="tinyMedicRestApp.vitals.liverFunction">Liver Function</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('liverFunction')} />
                </th>
                <th className="hand" onClick={sort('gfr')}>
                  <Translate contentKey="tinyMedicRestApp.vitals.gfr">Gfr</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('gfr')} />
                </th>
                <th className="hand" onClick={sort('cursor')}>
                  <Translate contentKey="tinyMedicRestApp.vitals.cursor">Cursor</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('cursor')} />
                </th>
                <th className="hand" onClick={sort('active')}>
                  <Translate contentKey="tinyMedicRestApp.vitals.active">Active</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('active')} />
                </th>
                <th className="hand" onClick={sort('microSec')}>
                  <Translate contentKey="tinyMedicRestApp.vitals.microSec">Micro Sec</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('microSec')} />
                </th>
                <th className="hand" onClick={sort('timestampentry')}>
                  <Translate contentKey="tinyMedicRestApp.vitals.timestampentry">Timestampentry</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('timestampentry')} />
                </th>
                <th className="hand" onClick={sort('lastupdated')}>
                  <Translate contentKey="tinyMedicRestApp.vitals.lastupdated">Lastupdated</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('lastupdated')} />
                </th>
                <th className="hand" onClick={sort('weight')}>
                  <Translate contentKey="tinyMedicRestApp.vitals.weight">Weight</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('weight')} />
                </th>
                <th className="hand" onClick={sort('height')}>
                  <Translate contentKey="tinyMedicRestApp.vitals.height">Height</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('height')} />
                </th>
                <th className="hand" onClick={sort('cvp')}>
                  <Translate contentKey="tinyMedicRestApp.vitals.cvp">Cvp</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('cvp')} />
                </th>
                <th className="hand" onClick={sort('map')}>
                  <Translate contentKey="tinyMedicRestApp.vitals.map">Map</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('map')} />
                </th>
                <th className="hand" onClick={sort('intake')}>
                  <Translate contentKey="tinyMedicRestApp.vitals.intake">Intake</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('intake')} />
                </th>
                <th className="hand" onClick={sort('output')}>
                  <Translate contentKey="tinyMedicRestApp.vitals.output">Output</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('output')} />
                </th>
                <th className="hand" onClick={sort('inOutUnits')}>
                  <Translate contentKey="tinyMedicRestApp.vitals.inOutUnits">In Out Units</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('inOutUnits')} />
                </th>
                <th>
                  <Translate contentKey="tinyMedicRestApp.vitals.glucoUser">Gluco User</Translate> <FontAwesomeIcon icon="sort" />
                </th>
                <th>
                  <Translate contentKey="tinyMedicRestApp.vitals.weightEntry">Weight Entry</Translate> <FontAwesomeIcon icon="sort" />
                </th>
                <th>
                  <Translate contentKey="tinyMedicRestApp.vitals.glucoseEntry">Glucose Entry</Translate> <FontAwesomeIcon icon="sort" />
                </th>
                <th>
                  <Translate contentKey="tinyMedicRestApp.vitals.appointment">Appointment</Translate> <FontAwesomeIcon icon="sort" />
                </th>
                <th>
                  <Translate contentKey="tinyMedicRestApp.vitals.volunteerVisit">Volunteer Visit</Translate> <FontAwesomeIcon icon="sort" />
                </th>
                <th>
                  <Translate contentKey="tinyMedicRestApp.vitals.bloodDonation">Blood Donation</Translate> <FontAwesomeIcon icon="sort" />
                </th>
                <th />
              </tr>
            </thead>
            <tbody>
              {vitalsList.map((vitals, i) => (
                <tr key={`entity-${i}`} data-cy="entityTable">
                  <td>
                    <Button tag={Link} to={`/vitals/${vitals.id}`} color="link" size="sm">
                      {vitals.id}
                    </Button>
                  </td>
                  <td>{vitals.systolic}</td>
                  <td>{vitals.diastolic}</td>
                  <td>
                    <Translate contentKey={`tinyMedicRestApp.BPMethod.${vitals.bpMethod}`} />
                  </td>
                  <td>{vitals.pulse}</td>
                  <td>
                    <Translate contentKey={`tinyMedicRestApp.OximetryMethod.${vitals.oximetryMethod}`} />
                  </td>
                  <td>{vitals.oximetry}</td>
                  <td>{vitals.respiratoryRate}</td>
                  <td>{vitals.bodyTemp}</td>
                  <td>{vitals.bodySurfaceArea}</td>
                  <td>{vitals.painScore}</td>
                  <td>{vitals.liverFunction}</td>
                  <td>{vitals.gfr}</td>
                  <td>{vitals.cursor}</td>
                  <td>{vitals.active ? 'true' : 'false'}</td>
                  <td>{vitals.microSec}</td>
                  <td>{vitals.timestampentry}</td>
                  <td>{vitals.lastupdated}</td>
                  <td>{vitals.weight}</td>
                  <td>{vitals.height}</td>
                  <td>{vitals.cvp}</td>
                  <td>{vitals.map}</td>
                  <td>{vitals.intake}</td>
                  <td>{vitals.output}</td>
                  <td>
                    <Translate contentKey={`tinyMedicRestApp.InOutUnits.${vitals.inOutUnits}`} />
                  </td>
                  <td>{vitals.glucoUser ? <Link to={`/gluco-user/${vitals.glucoUser.id}`}>{vitals.glucoUser.id}</Link> : ''}</td>
                  <td>{vitals.weightEntry ? <Link to={`/weight/${vitals.weightEntry.id}`}>{vitals.weightEntry.id}</Link> : ''}</td>
                  <td>{vitals.glucoseEntry ? <Link to={`/glucose/${vitals.glucoseEntry.id}`}>{vitals.glucoseEntry.id}</Link> : ''}</td>
                  <td>{vitals.appointment ? <Link to={`/appointments/${vitals.appointment.id}`}>{vitals.appointment.id}</Link> : ''}</td>
                  <td>
                    {vitals.volunteerVisit ? (
                      <Link to={`/volunteer-visits/${vitals.volunteerVisit.id}`}>{vitals.volunteerVisit.id}</Link>
                    ) : (
                      ''
                    )}
                  </td>
                  <td>
                    {vitals.bloodDonation ? <Link to={`/blood-donation/${vitals.bloodDonation.id}`}>{vitals.bloodDonation.id}</Link> : ''}
                  </td>
                  <td className="text-end">
                    <div className="btn-group flex-btn-group-container">
                      <Button tag={Link} to={`/vitals/${vitals.id}`} color="info" size="sm" data-cy="entityDetailsButton">
                        <FontAwesomeIcon icon="eye" />{' '}
                        <span className="d-none d-md-inline">
                          <Translate contentKey="entity.action.view">View</Translate>
                        </span>
                      </Button>
                      <Button
                        tag={Link}
                        to={`/vitals/${vitals.id}/edit?page=${paginationState.activePage}&sort=${paginationState.sort},${paginationState.order}`}
                        color="primary"
                        size="sm"
                        data-cy="entityEditButton"
                      >
                        <FontAwesomeIcon icon="pencil-alt" />{' '}
                        <span className="d-none d-md-inline">
                          <Translate contentKey="entity.action.edit">Edit</Translate>
                        </span>
                      </Button>
                      <Button
                        onClick={() =>
                          (window.location.href = `/vitals/${vitals.id}/delete?page=${paginationState.activePage}&sort=${paginationState.sort},${paginationState.order}`)
                        }
                        color="danger"
                        size="sm"
                        data-cy="entityDeleteButton"
                      >
                        <FontAwesomeIcon icon="trash" />{' '}
                        <span className="d-none d-md-inline">
                          <Translate contentKey="entity.action.delete">Delete</Translate>
                        </span>
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        ) : (
          !loading && (
            <div className="alert alert-warning">
              <Translate contentKey="tinyMedicRestApp.vitals.home.notFound">No Vitals found</Translate>
            </div>
          )
        )}
      </div>
      {totalItems ? (
        <div className={vitalsList && vitalsList.length > 0 ? '' : 'd-none'}>
          <div className="justify-content-center d-flex">
            <JhiItemCount page={paginationState.activePage} total={totalItems} itemsPerPage={paginationState.itemsPerPage} i18nEnabled />
          </div>
          <div className="justify-content-center d-flex">
            <JhiPagination
              activePage={paginationState.activePage}
              onSelect={handlePagination}
              maxButtons={5}
              itemsPerPage={paginationState.itemsPerPage}
              totalItems={totalItems}
            />
          </div>
        </div>
      ) : (
        ''
      )}
    </div>
  );
};

export default Vitals;
